/**
 * Integrytas Group - Base App (MOBILE)
 * Page: APG.TELEMETRY — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/ApgTelemetryPage.tsx. A DISTINCT
 * presentation (KPI tiles + scannable violation / bottleneck-pattern cards +
 * bottom-sheet detail) over the SAME read-only data layer — never the desktop
 * grid re-hosted (§16):
 *   - reads via apiDb over the APG telemetry tables (RLS scopes each read via
 *       the gateway JWT):
 *         apg_pattern    — aggregate slow-call fingerprints (hotspots)
 *         apg_violation  — recent verbatim slow-call breaches + captured params
 *   - resolves every violator surrogate user_id to a real display name via
 *       csm_user_master, so no raw id reaches render (§17.B4).
 *
 * This screen is READ-ONLY: it performs no writes (the desktop's pattern-triage
 * and threshold-override editing are omitted on mobile), so it imports neither
 * routed_writes nor any mutation path (§2 — there is simply nothing to write).
 */

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Activity, Gauge, Clock } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobilePillTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// Map a legacy m-p-* pill class to the MobileStatusPill tone.
const PILL_TONE_BY_CLASS: Record<string, MobilePillTone> = {
  'm-p-ok': 'ok', 'm-p-no': 'no', 'm-p-wait': 'wait', 'm-p-info': 'info', 'm-p-you': 'you',
};
function pillTone(cls: string | undefined): MobilePillTone {
  return (cls && PILL_TONE_BY_CLASS[cls]) || 'info';
}

// ---------------------------------------------------------------------------
// Row shapes (mirror the desktop columns exactly — do not invent columns).
// ---------------------------------------------------------------------------

interface PatternRow {
  apg_pattern_id: number;
  fingerprint: string;
  request_kind: string | null;
  endpoint: string | null;
  measured_class: string | null;
  occurrences: number;
  max_duration_ms: number;
  sum_duration_ms: number;
  last_duration_ms: number | null;
  last_seen: string;
  status: string;
  notes: string | null;
}

interface ViolationRow {
  apg_violation_id: number;
  request_kind: string | null;
  endpoint: string | null;
  measured_class: string | null;
  duration_ms: number;
  threshold_ms: number | null;
  params: unknown;
  screen_code: string | null;
  une_workspace_id: number | null;
  user_id: number | null;
  created_at: string;
}

/** A violation row enriched with its resolved violator display name. */
interface ViolationView extends ViolationRow {
  user_name: string;
}

type ViewFacet = 'violations' | 'patterns';

/** Discriminated selection for the detail bottom-sheet. */
type Selected =
  | { kind: 'violation'; row: ViolationView }
  | { kind: 'pattern'; row: PatternRow };

// ---------------------------------------------------------------------------
// CHECK-enum label constants (§17.B11) — the stored value stays the code; only
// the displayed label is localised, routed through t(), never a bare literal.
// ---------------------------------------------------------------------------

const PATTERN_STATUS: Record<string, { labelKey: string; fallback: string; pill: string }> = {
  OPEN: { labelKey: 'mobile.apg.status.OPEN', fallback: 'Open', pill: 'm-p-wait' },
  ACKNOWLEDGED: { labelKey: 'mobile.apg.status.ACKNOWLEDGED', fallback: 'Acknowledged', pill: 'm-p-info' },
  RESOLVED: { labelKey: 'mobile.apg.status.RESOLVED', fallback: 'Resolved', pill: 'm-p-ok' },
};

const PATTERN_LIMIT = 100;
const VIOLATION_LIMIT = 50;

/** Numeric params block styling (read-only JSON dump, no shared CSS to add). */
const PARAMS_BLOCK: CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '11px',
  maxHeight: '9rem',
  overflow: 'auto',
  margin: 0,
};

export function MobileApgTelemetryPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [patterns, setPatterns] = useState<PatternRow[]>([]);
  const [violations, setViolations] = useState<ViolationView[]>([]);
  const [view, setView] = useState<ViewFacet>('violations');
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Selected | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [p, v] = await Promise.all([
        apiDb
          .from('apg_pattern')
          .select('apg_pattern_id, fingerprint, request_kind, endpoint, measured_class, occurrences, max_duration_ms, sum_duration_ms, last_duration_ms, last_seen, status, notes')
          .order('occurrences', { ascending: false })
          .limit(PATTERN_LIMIT),
        apiDb
          .from('apg_violation')
          .select('apg_violation_id, request_kind, endpoint, measured_class, duration_ms, threshold_ms, params, screen_code, une_workspace_id, user_id, created_at')
          .order('created_at', { ascending: false })
          .limit(VIOLATION_LIMIT),
      ]);
      if (p.error) throw p.error;
      if (v.error) throw v.error;

      setPatterns((p.data ?? []) as PatternRow[]);

      const rawViols = (v.data ?? []) as ViolationRow[];
      // Resolve every violator FK to a real name so no surrogate id reaches
      // render (§17.B4). One batched lookup, then map over the violation rows.
      const ids = Array.from(
        new Set(rawViols.map((x) => x.user_id).filter((x): x is number => x != null)),
      );
      const names: Record<number, string> = {};
      if (ids.length) {
        const { data: us, error: uErr } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', ids);
        if (uErr) throw uErr;
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) {
          names[u.user_id] = u.display_name || u.email || '—';
        }
      }
      setViolations(
        rawViols.map((x) => ({
          ...x,
          user_name: x.user_id == null ? '—' : (names[x.user_id] ?? '—'),
        })),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  // KPI roll-ups across the loaded telemetry (§ HomeScreen KPI pattern).
  const kpis = useMemo(() => {
    const openPatterns = patterns.filter((p) => p.status === 'OPEN').length;
    const slowestMs = patterns.reduce((m, p) => Math.max(m, p.max_duration_ms || 0), 0);
    return {
      violations: violations.length,
      patterns: patterns.length,
      openPatterns,
      slowestMs: Math.round(slowestMs),
    };
  }, [patterns, violations]);

  const endpointLabel = (kind: string | null, endpoint: string | null): string =>
    `${kind ?? '—'}:${endpoint ?? '—'}`;

  const filteredViolations = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return violations;
    return violations.filter(
      (v) =>
        endpointLabel(v.request_kind, v.endpoint).toLowerCase().includes(q) ||
        (v.measured_class ?? '').toLowerCase().includes(q) ||
        v.user_name.toLowerCase().includes(q),
    );
  }, [violations, filter]);

  const filteredPatterns = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return patterns;
    return patterns.filter(
      (p) =>
        endpointLabel(p.request_kind, p.endpoint).toLowerCase().includes(q) ||
        (p.measured_class ?? '').toLowerCase().includes(q),
    );
  }, [patterns, filter]);

  const viewFacets: { key: ViewFacet; label: string }[] = [
    { key: 'violations', label: t('mobile.apg.recent_violations', 'Recent violations') },
    { key: 'patterns', label: t('mobile.apg.bottleneck_patterns', 'Bottleneck patterns') },
  ];

  const statusMeta = (status: string) =>
    PATTERN_STATUS[status] ?? { labelKey: `mobile.apg.status.${status}`, fallback: status, pill: 'm-p-info' };

  const classLabel = (c: string | null): string =>
    c == null ? '—' : t(`mobile.apg.class.${c}`, c);

  const avgMs = (p: PatternRow): number => (p.occurrences ? Math.round(p.sum_duration_ms / p.occurrences) : 0);

  const paramsText = (value: unknown): string => {
    try {
      const s = JSON.stringify(value, null, 2);
      return s == null ? '—' : s;
    } catch {
      return '—';
    }
  };

  const isViolationView = view === 'violations';
  const emptyList = isViolationView ? filteredViolations.length === 0 : filteredPatterns.length === 0;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.apg.title', 'Performance Telemetry')}
        description={t('mobile.apg.description', 'Recent slow-call breaches and the bottleneck patterns behind them.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.violations.toLocaleString(), label: t('mobile.apg.kpi.violations', 'Violations') },
          { value: kpis.patterns.toLocaleString(), label: t('mobile.apg.kpi.patterns', 'Patterns') },
          { value: kpis.openPatterns.toLocaleString(), label: t('mobile.apg.kpi.open', 'Open') },
          { value: kpis.slowestMs.toLocaleString(), label: t('mobile.apg.kpi.slowest_ms', 'Slowest ms') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={
            isViolationView
              ? t('mobile.apg.search_violations', 'Search violations')
              : t('mobile.apg.search_patterns', 'Search patterns')
          }
          aria-label={
            isViolationView
              ? t('mobile.apg.search_violations', 'Search violations')
              : t('mobile.apg.search_patterns', 'Search patterns')
          }
        />
      </div>

      <MobileFilterChips
        chips={viewFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => view === v}
        onSelect={(v) => { setView(v as ViewFacet); setFilter(''); }}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {emptyList && !busy ? (
        <MobileContentCard>
          <div className="m-empty">
            <Inbox />{' '}
            {isViolationView
              ? t('mobile.apg.no_violations', 'No violations')
              : t('mobile.apg.no_patterns', 'No slow-call patterns')}
          </div>
        </MobileContentCard>
      ) : isViolationView ? (
        <MobileContentCard list>
          {filteredViolations.map((v) => (
            <MobileCardRow
              key={v.apg_violation_id}
              lead={<Activity />}
              leadTone="red"
              title={endpointLabel(v.request_kind, v.endpoint)}
              subtitle={`${classLabel(v.measured_class)} · ${formatDateTime(v.created_at)} · ${v.user_name}`}
              trailing={
                <MobileStatusPill tone="no">
                  {t('mobile.apg.ms_value', { ms: Math.round(v.duration_ms), defaultValue: `${Math.round(v.duration_ms)} ms` })}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected({ kind: 'violation', row: v })}
            />
          ))}
        </MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filteredPatterns.map((p) => {
            const meta = statusMeta(p.status);
            return (
              <MobileCardRow
                key={p.apg_pattern_id}
                lead={<Gauge />}
                leadTone={p.status === 'OPEN' ? 'amber' : 'slate'}
                title={endpointLabel(p.request_kind, p.endpoint)}
                subtitle={
                  t('mobile.apg.hits_n', { count: p.occurrences, defaultValue: `${p.occurrences} hits` })
                  + ' · '
                  + t('mobile.apg.max_ms', { ms: Math.round(p.max_duration_ms), defaultValue: `${Math.round(p.max_duration_ms)} ms max` })
                }
                trailing={<MobileStatusPill tone={pillTone(meta.pill)}>{t(meta.labelKey, meta.fallback)}</MobileStatusPill>}
                chevron
                onClick={() => setSelected({ kind: 'pattern', row: p })}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.apg.detail', 'Telemetry detail')}>
        <div className="m-grabber" />
        {selected?.kind === 'violation' && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Activity size={20} /></span>
              <span>
                <div className="m-nm">{endpointLabel(selected.row.request_kind, selected.row.endpoint)}</div>
                <div className="m-em">{formatDateTime(selected.row.created_at)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.apg.field.class', 'Class')} value={classLabel(selected.row.measured_class)} />
              <MobileDetailRow
                label={t('mobile.apg.field.duration', 'Duration')}
                value={<MobileStatusPill tone="no">{t('mobile.apg.ms_value', { ms: Math.round(selected.row.duration_ms), defaultValue: `${Math.round(selected.row.duration_ms)} ms` })}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.apg.field.threshold', 'Threshold')}
                value={selected.row.threshold_ms == null ? '—' : t('mobile.apg.ms_value', { ms: Math.round(selected.row.threshold_ms), defaultValue: `${Math.round(selected.row.threshold_ms)} ms` })}
              />
              {/* fk-display-ok: screen_code is a raw perf-telemetry screen tag, not a FK to a named entity */}
              <MobileDetailRow label={t('mobile.apg.field.screen', 'Screen')} value={selected.row.screen_code ?? '—'} />
              <MobileDetailRow label={t('mobile.apg.field.user', 'User')} value={selected.row.user_name} />
            </MobileContentCard>
            <MobileContentCard>
              <div className="m-field" style={{ display: 'block' }}>
                <span className="m-k">{t('mobile.apg.field.params', 'Captured params')}</span>
                <pre style={PARAMS_BLOCK}>{paramsText(selected.row.params)}</pre>
              </div>
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
        {selected?.kind === 'pattern' && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Gauge size={20} /></span>
              <span>
                <div className="m-nm">{endpointLabel(selected.row.request_kind, selected.row.endpoint)}</div>
                <div className="m-em">{classLabel(selected.row.measured_class)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.apg.field.status', 'Status')}
                value={<MobileStatusPill tone={pillTone(statusMeta(selected.row.status).pill)}>{t(statusMeta(selected.row.status).labelKey, statusMeta(selected.row.status).fallback)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.apg.field.hits', 'Hits')} value={selected.row.occurrences.toLocaleString()} />
              <MobileDetailRow label={t('mobile.apg.field.max_ms', 'Max ms')} value={Math.round(selected.row.max_duration_ms).toLocaleString()} />
              <MobileDetailRow label={t('mobile.apg.field.avg_ms', 'Avg ms')} value={avgMs(selected.row).toLocaleString()} />
              <MobileDetailRow label={t('mobile.apg.field.last_ms', 'Last ms')} value={selected.row.last_duration_ms == null ? '—' : Math.round(selected.row.last_duration_ms).toLocaleString()} />
              <MobileDetailRow
                label={t('mobile.apg.field.last_seen', 'Last seen')}
                value={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> {formatDateTime(selected.row.last_seen)}</span>}
              />
            </MobileContentCard>
            {selected.row.notes && (
              <MobileContentCard>
                <div className="m-field" style={{ display: 'block' }}>
                  <span className="m-k">{t('mobile.apg.field.notes', 'Notes')}</span>
                  <div className="m-v" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selected.row.notes}</div>
                </div>
              </MobileContentCard>
            )}
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
